import { memo } from "react";
import Button from "@mui/material/Button";
import styles from "./editGroup.module.scss";
import { Avatar } from "@mui/material";
import { Link } from "react-router-dom";
import { bgColorbyName } from "src/variable/randomColor";
import { EMPTY_STRING } from "src/components/constant/constant";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface ButtonConfig {
  text?: string;
  visible?: boolean;
  onClick?: () => void;
}

interface CardContainerProps {
  title: string;
  subTitle?: string;
  buttons?: {
    primaryButton?: ButtonConfig;
    secondaryButton?: ButtonConfig;
  };
  children: React.ReactNode;
  cardContainerStyle?: string;
}

interface MemberCardProps {
  link?: string;
  name: string;
  secondaryInfo: string;
  role?: string;
  roleColorValue?: string;
  onDelete?: () => void;
}

export const MemberCard = memo(
  ({ link = "#", name, secondaryInfo, role, roleColorValue, onDelete }: MemberCardProps) => {
    const handleDelete = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete?.();
    };

    return (
      <main className={styles.memberCard}>
        <Link to={link} className={styles.memberLink}>
          <Avatar aria-label={`Avatar for ${name}`} style={{ backgroundColor: bgColorbyName(name ?? EMPTY_STRING) }}>
            {name?.charAt(0)?.toUpperCase() ?? EMPTY_STRING}
          </Avatar>
          <div className={styles.info}>
            <div className={styles.name}>{name}</div>
            <div className={styles.secondaryInfo}>{secondaryInfo}</div>
          </div>
          <div className={styles.actionBtn}>
            {role && (
              <div
                className={styles.role}
                style={{
                  backgroundColor: `${roleColorValue}33`,
                  borderColor: `${roleColorValue}73`,
                  color: `${roleColorValue}FF`
                }}
              >
                {role}
              </div>
            )}
            {onDelete && (
              <button className={styles.edit} onClick={handleDelete} aria-label={`Remove ${name}`}>
                <DeleteOutlineIcon className={styles.editIcon} />
              </button>
            )}
          </div>
        </Link>
      </main>
    );
  }
);

MemberCard.displayName = "MemberCard";

export const CardContainer = memo(
  ({ title, subTitle, cardContainerStyle, buttons = {}, children }: CardContainerProps) => {
    const {
      primaryButton = { text: "Primary Button", visible: false },
      secondaryButton = { text: "Secondary Button", visible: false }
    } = buttons;

    return (
      <div className={`${styles.cardContainer} ${cardContainerStyle}`}>
        <header className={styles.header}>
          <div className={styles.info}>
            <h3 className={styles.title}>{title}</h3>
            {subTitle && <p className={styles.subTitle}>{subTitle}</p>}
          </div>
          <div className={styles.actionButton}>
            {primaryButton.visible && (
              <Button variant="contained" className={styles.primaryButton} onClick={primaryButton.onClick}>
                {primaryButton.text}
              </Button>
            )}
            {secondaryButton.visible && (
              <Button variant="outlined" className={styles.secondaryButton} onClick={secondaryButton.onClick}>
                {secondaryButton.text}
              </Button>
            )}
          </div>
        </header>
        {children}
      </div>
    );
  }
);

CardContainer.displayName = "CardContainer";
